import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import { createRefPath_event } from "global/firestoreData/appData/appRefPaths";
import _ from 'lodash';
import { useCallback, useContext } from 'react';
import { ParentContext } from "../ParentContext";
import { convertHelpers } from "global/common/convert";

export const useMultiDoc = () => {

  const parentContext = useContext(ParentContext)
  const { states } = parentContext ?? {}
  const { app_state } = states ?? {}
  const { mainDb } = app_state ?? {}

  const handleGet_multiDocs = useCallback(({
    key,
    sportsDocs,
    pathViews,
    pks,
    callback
  }) => {
    listenToViewDocs({ key, mainDb, sportsDocs, pathViews, pks, callback })
  }, []);

  return { handleGet_multiDocs }

}

const listenToViewDocs = ({
  key,
  mainDb,
  sportsDocs,
  pathViews,
  pks,
  callback
}) => {
  const unsubs = []
  const cache = {}

  const emit = () => {
    // only emit when all main docs and all expected subcollections are loaded  
    if (_.size(cache) !== _.size(sportsDocs)) return

    const allReady = _.every(cache, entry => {
      // expectedSubs must be ready
      if (entry.expectedSubs > 0 &&
        _.size(entry.subs) !== entry.expectedSubs) {
        return false
      }
      // main has been checked (either real doc or 'empty')
      if (_.isEmpty(entry.main)) {
        return false
      }
      return true
    })

    if (!allReady) return
    const readyEntries = _.pickBy(cache, entry => entry.main !== 'empty')
    _.forEach(readyEntries, (re) => {
      const { subs, sportsDoc } = re ?? {}
      if (sportsDoc?.combineSubs) {
        combineData(subs, sportsDoc)
      }
    })
    const { fullData, _parentDataKeys } = ammendData(readyEntries)
    callback(fullData, _parentDataKeys)
  }

  _.forEach(sportsDocs, (sportsDoc, sportsDocKey) => {

    const { collectionName, direct, normalCollection, noYear } = sportsDoc
    const _direct = direct || normalCollection
    const refPath = createRefPath_event(pathViews, _direct ? [collectionName, pathViews.sports] : [`_${collectionName}`])

    const colRef = _direct ? doc(mainDb, refPath) : collection(mainDb, refPath)

    const wheres = []
    _.forEach(pks, (pk, pkk) => {
      if (noYear) {
        if (pkk !== 'sportsYears') {
          wheres.push(where(`parentKeys.${pkk}`, '==', pk))
        }
      } else {
        wheres.push(where(`parentKeys.${pkk}`, '==', pk))
      }
    })

    const q = _direct ? doc(mainDb, refPath) : query(colRef, ...wheres)

    // Initialize cache entry
    cache[collectionName] = {
      main: null,
      subs: {},
      expectedSubs: sportsDoc.collectionSubs?.length || 0,
      refPath,
      sportsDoc
    }

    // Listen to main doc
    const unsubMain = onSnapshot(q, (snap) => {
      const doc = _direct ? snap : snap.docs[0]
      cache[collectionName].main = doc
        ? { id: doc.id, ...doc.data() }
        : 'empty'   // sentinel value

      if (_direct) {
        const docItem = doc.data()
        currentSeason = docItem?.currentSeason
      }

      // Attach subcollection listeners ONLY if collectionSubs exists
      if (Array.isArray(sportsDoc.collectionSubs) && sportsDoc.collectionSubs.length) {
        _.forEach(sportsDoc.collectionSubs, (subColName) => {
          if (cache[collectionName].subs[subColName] || !doc) {
            cache[collectionName].subs[subColName] = 'empty'
            return
          }

          const subColRef = collection(doc.ref, subColName)

          const unsubSub = onSnapshot(subColRef, (subSnap) => {
            cache[collectionName].subs[subColName] =
              subSnap.docs.map(d => ({
                docKey: d.id,
                ...d.data()
              }))

            emit()
          })

          unsubs.push(unsubSub)
        })
      }

      // Emit immediately if there are no subcollections
      emit()
    })

    unsubs.push(unsubMain)
  })

  return () => {
    _.forEach(unsubs, u => u())
  }
}

const ammendData = (dataSet) => {
  const fullData = {}
  const _parentDataKeys = {}

  _.forEach(dataSet, (data, dataKey) => {
    const { sportsDoc, main, subs } = data ?? {}
    const { sportPropName, collectionName, renderAs } = sportsDoc ?? {}

    const fdKey = sportPropName || collectionName || dataKey
    fullData[fdKey] = {}

    if (main) {
      _.forEach(main, (mainItem, mainKey) => {
        switch (mainKey) {
          case 'id':
            _parentDataKeys[fdKey] = mainItem
            break;
          case 'parentKeys':
            // nothing
            break;
          default:
            fullData[fdKey][mainKey] = mainItem
        }
      })
    }

    if (!_.isEmpty(subs) && sportsDoc?.combineSubs) {
      const dc = combineData(subs, sportsDoc)
      _.forEach(dc, (dci, dck) => {
        fullData[fdKey][dck] = dci
      })
    }
  })
  return { fullData, _parentDataKeys }
}

const combineData = (subs, sportsDoc) => {
  const { subItemKey } = sportsDoc ?? {}
  if (subs) {
    const combinedData = _.mapValues(subs, (subDocs, subKey) => {
      return _.reduce(subDocs, (combinedData, subItem) => {
        const { docKey } = subItem
        const subData = subItem[subKey] || {}
        if (subItemKey) {
          convertHelpers.createItemPropValues(subData, subItemKey, docKey)
        }
        return { ...combinedData, ...subData }
      }, {})
    })
    return combinedData
  }
}